import AsyncStorage from '@react-native-async-storage/async-storage';
import moment from 'moment';
import { findIndex } from 'lodash';

import { OpenMeteoWeatherCode, WeatherForecast as OpenMeteoWeatherForecast } from '../types';

/**
 * Represents a single hour's weather forecast data.
 */
export interface HourlyForecast {
  isDay?: boolean;
  time: string;
  temperature?: string;
  precipitation?: string;
  weatherCode?: OpenMeteoWeatherCode;
}

/**
 * Transformed weather forecast data optimized for display.
 * This format is simplified from the OpenMeteo API response and includes
 * formatted strings ready for UI rendering.
 */
export interface WeatherForecast {
  code?: OpenMeteoWeatherCode;
  isDay?: boolean;
  sunset?: string;
  sunrise?: string;
  currentTemp?: string;
  precipitation?: string;
  maxTemp?: string;
  minTemp?: string;
  time?: string;
  hourly?: HourlyForecast[];
  version: number;
}

/**
 * Weather Forecast Store
 *
 * A lightweight storage solution for weather forecast data using AsyncStorage
 * (local persistent storage) instead of in-memory state management.
 *
 * **Design Rationale:**
 * This store is intentionally designed to use AsyncStorage rather than global
 * state management (e.g., Redux, Zustand, Context API) to optimize memory usage.
 * Since weather forecast data is only needed within a single screen/widget and
 * is not shared across multiple components or screens, keeping it in memory would
 * be wasteful. By persisting to AsyncStorage:
 *
 * - **Memory Efficiency**: Data is stored on disk, not in RAM, freeing up memory
 *   for other application needs
 * - **Single Screen Scope**: Weather data is scoped to the weather widget/screen
 *   and doesn't need to be accessible globally
 * - **Persistence**: Data persists across app restarts, providing offline access
 *   to cached forecasts
 * - **Simplified State**: Avoids the overhead of global state management for
 *   data that doesn't need to be reactive or shared
 */
export class WeatherForecastStore {
  /**
   * Current version of the stored data format.
   * Increment this when the WeatherForecast interface changes to enable
   * migration logic in the future.
   */
  static readonly version: number = 1;

  /**
   * Builds the storage key for a given site ID.
   * @param siteId - Unique identifier for the site/location
   * @returns Storage key in format: `weather-forecast:{siteId}`
   */
  private static buildKey(siteId: string) {
    return `weather-forecast:${siteId}`;
  }

  static async get(siteId: string): Promise<WeatherForecast | undefined> {
    try {
      const value = await AsyncStorage.getItem(this.buildKey(siteId));
      if (value) {
        const jsonData = JSON.parse(value);
        return jsonData;
      }
      return undefined;
    } catch (error) {
      return undefined;
    }
  }

  static async set(siteId: string, data: OpenMeteoWeatherForecast, utcOffsetInMinutes?: number): Promise<WeatherForecast> {
    const today = new Date().targetOffset(utcOffsetInMinutes);
    const isoDate = moment(today).format().slice(0, 19);
    const { current, current_units, daily, daily_units, hourly, hourly_units } = data;
    const index = findIndex(daily?.time, _date => _date === isoDate.slice(0, 10));

    const weather: WeatherForecast = {
      version: this.version,
      code: current?.weather_code,
      currentTemp: `${current?.temperature_2m}${current_units?.temperature_2m}`,
      precipitation: `${current?.precipitation_probability}${current_units?.precipitation_probability}`,
      isDay: current?.is_day === 1,
      time: current?.time,
      hourly: hourly?.time?.reduce((forecast: HourlyForecast[], time, index) => {
        const temperature = hourly.temperature_2m[index];
        const precipitation = hourly.precipitation_probability[index];
        const dateTime = moment(time);

        // Skip past and current hour
        if (dateTime.toDate() <= today) return forecast;

        return [
          ...forecast,
          {
            time: dateTime.format('h:mm A'),
            isDay: hourly.is_day[index] === 1,
            temperature: typeof temperature === 'number' ? `${temperature}${hourly_units?.temperature_2m}` : undefined,
            precipitation: typeof precipitation === 'number' ? `${precipitation}${hourly_units?.precipitation_probability}` : undefined,
            weatherCode: hourly.weather_code[index] as OpenMeteoWeatherCode,
          },
        ];
      }, []),
    };

    if (index !== -1) {
      const sunrise = daily?.sunrise[index];
      const sunset = daily?.sunset[index];
      const maxTemp = daily?.temperature_2m_max[index];
      const minTemp = daily?.temperature_2m_min[index];

      if (typeof maxTemp === 'number') {
        weather.maxTemp = `${maxTemp}${daily_units?.temperature_2m_max}`;
      }

      if (typeof minTemp === 'number') {
        weather.minTemp = `${minTemp}${daily_units?.temperature_2m_min}`;
      }

      if (sunrise) {
        weather.sunrise = moment(sunrise).format('h:mm A');
      }
      if (sunset) {
        weather.sunset = moment(sunset).format('h:mm A');
      }
    }

    // cache the weather forecast
    const jsonString = JSON.stringify(weather);
    await AsyncStorage.setItem(this.buildKey(siteId), jsonString);

    return weather;
  }
}
